import { Css, Tag } from "@homebound/beam";
import { ChangeEventType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { SwapProductInCatalogFormInput } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/inCatalog/swapProductInCatalogWorkflow";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { changeEventTypeToNameMapper, fail, queriesResult } from "src/utils";
import { ReviewImpactSection } from "../../../components/ReviewImpactSection";
import { ScopeChangesTable } from "../../../components/ScopeChangesTable";
import { WorkflowFormState } from "../../types";
import { useReviewImpactChangeEventQuery, useReviewImpactProjectItemCountQuery } from "./2.ReviewImpactStep.generated";
import { BudgetAndScopeImpactCards } from "./components/BudgetAndScopeImpactCards";
import { ProductChangesTable } from "./components/ProductChangesTable";

type ReviewImpactStepProps = { formState: WorkflowFormState<SwapProductInCatalogFormInput> };

export function ReviewImpactStep(props: ReviewImpactStepProps) {
  const { formState } = props;
  const { projectId, idOrAdd } = useRouteParams<GuidedChangeEventParams>();
  const changeEventId = idOrAdd ?? fail("No change event id");
  const changeEventQuery = useReviewImpactChangeEventQuery({ variables: { changeEventId } });
  const projectItemCountQuery = useReviewImpactProjectItemCountQuery({
    // Only the count is used, so ask for the smallest page the backend will give us.
    variables: { filter: { project: [projectId], isRemovedScope: false }, page: { offset: 0, limit: 1 } },
    // `projectItemsPage` has an infinite-scroll type policy that appends pages, which would skew the count.
    fetchPolicy: "no-cache",
  });

  return queriesResult([changeEventQuery, projectItemCountQuery] as const, {
    data: ({ changeEvent }, projectItemCountData) => {
      const clientNoun = changeEvent.project.lotType.clientNoun;
      const isChargeable = changeEvent.type.code === ChangeEventType.External;
      return (
        <div css={Css.df.fdc.py3.gap8.$}>
          <ReviewImpactSection
            title="Review Scope & Budget Impact"
            description="Review the work that the selected product(s) will add to the project, its budget impact to Homebound, and its price impact to the homeowner."
          >
            <BudgetAndScopeImpactCards
              changeEvent={changeEvent}
              currentProjectItemCount={projectItemCountData.projectItemsPage.pageInfo.totalCount}
              isChargeable={isChargeable}
            />
          </ReviewImpactSection>

          <ReviewImpactSection
            title="Product Changes"
            fullBleed
            description={
              <div css={Css.df.aic.gap1.$}>
                <Tag
                  data-testid="changeEventType"
                  type={isChargeable ? "success" : "neutral"}
                  text={
                    isChargeable ? `Chargeable to ${clientNoun}` : changeEventTypeToNameMapper(changeEvent.type.code)
                  }
                />
                <span>
                  The following product replacements will be made.
                  {isChargeable && ` Add ${clientNoun} price.`}
                </span>
              </div>
            }
          >
            <ProductChangesTable formState={formState} changeEvent={changeEvent} isChargeable={isChargeable} />
          </ReviewImpactSection>

          <ReviewImpactSection
            title="Scope Changes"
            fullBleed
            description="The selected option(s) above will add the following line items below to this project."
          >
            <ScopeChangesTable changeEvent={changeEvent} />
          </ReviewImpactSection>
        </div>
      );
    },
  });
}
